/**
 * WebAuthn helpers for biometric vault unlock.
 */

export function isWebAuthnAvailable() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnAvailable()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Start biometric registration with the given server options.
 * Returns the credential response ready to POST back to the server.
 */
export async function registerBiometric(serverOptions) {
  if (!serverOptions || !serverOptions.challenge || !serverOptions.user?.id) {
    console.error('[webauthn] registerBiometric: invalid server options', serverOptions);
    throw new Error('invalid_server_options');
  }

  let publicKey;
  try {
    publicKey = {
      ...serverOptions,
      challenge: base64urlToBuffer(serverOptions.challenge),
      user: {
        ...serverOptions.user,
        id: base64urlToBuffer(serverOptions.user.id),
      },
      excludeCredentials: (serverOptions.excludeCredentials || []).map(c => ({
        ...c,
        id: base64urlToBuffer(c.id),
      })),
    };
  } catch (e) {
    console.error('[webauthn] failed to decode server options', e);
    throw new Error('decode_failed');
  }

  console.log('[webauthn] calling navigator.credentials.create()', {
    rpId: publicKey.rp?.id,
    userVerification: publicKey.authenticatorSelection?.userVerification,
    attachment: publicKey.authenticatorSelection?.authenticatorAttachment,
  });

  let credential;
  try {
    credential = await navigator.credentials.create({ publicKey });
  } catch (e) {
    // Preserve DOMException name/message so the caller can surface a
    // useful toast (NotAllowedError, SecurityError, AbortError, etc.)
    console.error('[webauthn] credentials.create() failed:', e?.name, e?.message, e);
    throw e;
  }

  if (!credential) {
    console.error('[webauthn] credentials.create() returned null');
    throw new Error('no_credential_returned');
  }

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || 'platform',
  };
}

/**
 * Start biometric authentication with the given server options.
 * Returns the assertion response ready to POST back to the server.
 */
export async function authenticateBiometric(serverOptions) {
  const publicKey = {
    ...serverOptions,
    challenge: base64urlToBuffer(serverOptions.challenge),
    allowCredentials: (serverOptions.allowCredentials || []).map(c => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  const assertion = await navigator.credentials.get({ publicKey });

  return {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    response: {
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : null,
    },
    type: assertion.type,
    authenticatorAttachment: assertion.authenticatorAttachment || 'platform',
  };
}
